/**
 * Import reálných jízdních řádů ve formátu GTFS (zip) do interní sítě TransitNetwork.
 *
 * Zásady:
 *  - žádná síťová volání, žádné side-effecty — čistá transformace bajtů na data,
 *  - vlastní robustní CSV parser: uvozovky, čárky v uvozovkách, CRLF, BOM,
 *    sloupce mapované podle názvů v hlavičce (ne podle pozic),
 *  - výkon: stop_times.txt se čte JEDNÍM průchodem se skupinováním podle trip_id;
 *    časová i paměťová složitost je O(počet řádků) — žádné vnořené průchody.
 *    (Setřídění zastávek uvnitř tripu je O(k log k) pro k zastávek tripu,
 *    v souhrnu tedy prakticky lineární vůči velikosti souboru.)
 */
import { strFromU8, unzipSync } from 'fflate';
import { haversineKm } from '../core/geo';
import type {
  TransitLine,
  TransitNetwork,
  TransitStop,
  TransitVehicle,
} from '../core/types';

export interface GtfsImportResult {
  network: TransitNetwork;
  stats: { stops: number; lines: number; trips: number };
  warnings: string[];
  feedName?: string;
}

// ---------------------------------------------------------------------------
// CSV parser
// ---------------------------------------------------------------------------

interface CsvTable {
  /** Index sloupce podle názvu z hlavičky (trim + lowercase). */
  cols: Map<string, number>;
  rows: string[][];
}

/**
 * Robustní CSV parser podle RFC 4180: uvozovkované hodnoty, zdvojené uvozovky,
 * čárky i nové řádky uvnitř uvozovek, CRLF/LF/CR konce řádků, BOM na začátku.
 */
function parseCsv(text: string): CsvTable {
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1); // BOM
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"'; // zdvojená uvozovka = literál
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
      if (ch === '\r' && text[i + 1] === '\n') i++; // CRLF
    } else {
      field += ch;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Prázdné řádky (typicky poslední) vynecháme.
  const nonEmpty = rows.filter((r) => r.length > 1 || (r.length === 1 && r[0].trim() !== ''));
  const header = nonEmpty.shift() ?? [];
  const cols = new Map<string, number>();
  header.forEach((h, idx) => cols.set(h.trim().toLowerCase(), idx));
  return { cols, rows: nonEmpty };
}

/** Přístup k hodnotě sloupce podle názvu; chybějící sloupec → vždy ''. */
function getter(table: CsvTable, name: string): (row: string[]) => string {
  const idx = table.cols.get(name);
  if (idx === undefined) return () => '';
  return (row) => (row[idx] ?? '').trim();
}

// ---------------------------------------------------------------------------
// Pomocné funkce
// ---------------------------------------------------------------------------

/** GTFS čas „HH:MM:SS" (smí být > 24:00) → minuty od půlnoci; null pro neplatný vstup. */
function parseGtfsTimeMin(s: string): number | null {
  const m = /^(\d{1,3}):(\d{2})(?::(\d{2}))?$/.exec(s.trim());
  if (!m) return null;
  const min = Number(m[2]);
  const sec = m[3] ? Number(m[3]) : 0;
  if (min > 59 || sec > 59) return null;
  return Number(m[1]) * 60 + min + sec / 60;
}

/** Minuty od půlnoci → „HH:MM"; časy > 24 h normalizuje modulo 24 h. */
function toHHMM(min: number): string {
  const m = ((Math.floor(min) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** Dnešní datum v Europe/Prague jako 'YYYY-MM-DD' (en-CA dává ISO formát). */
function todayPrague(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Prague' }).format(new Date());
}

const WEEKDAY_COLS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

/** Název sloupce calendar.txt pro den v týdnu daného data. */
function weekdayCol(dateIso: string): string {
  const [y, mo, d] = dateIso.split('-').map(Number);
  return WEEKDAY_COLS[new Date(Date.UTC(y, mo - 1, d)).getUTCDay()];
}

/** Mapování GTFS route_type na interní druh vozidla. */
function vehicleFromRouteType(routeType: string): TransitVehicle {
  switch (Number(routeType)) {
    case 0:
      return 'tram';
    case 1:
      return 'metro';
    case 2:
      return 'train';
    case 3:
      return 'bus';
    case 4:
      return 'ferry';
    case 5: // kabelová tramvaj
    case 6: // visutá lanovka
    case 7: // pozemní lanovka
    case 12: // monorail — kolejová doprava, chová se jako tram
      return 'tram';
    case 11: // trolejbus — provozně autobus
      return 'bus';
    default:
      return 'bus';
  }
}

/** Emise g CO₂/os·km podle druhu vozidla. */
const EMISSIONS: Record<TransitVehicle, number> = {
  bus: 80,
  tram: 35,
  metro: 30,
  train: 35,
  ferry: 250,
};

/** Komfort 1–5 podle druhu vozidla. */
const COMFORT: Record<TransitVehicle, number> = {
  bus: 3,
  tram: 3.5,
  metro: 4,
  train: 4,
  ferry: 4,
};

/** Deterministická paleta pro linky bez platné route_color. */
const PALETTE = [
  '#d32f2f',
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#7b1fa2',
  '#0097a7',
  '#c2185b',
  '#5d4037',
  '#455a64',
  '#afb42b',
  '#e64a19',
  '#303f9f',
] as const;

/** Medián hodnot; null pro prázdný vstup. */
function median(xs: number[]): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** Najde soubor v archivu — přesný název, nebo v podadresáři (některé feedy balí složku). */
function findFile(files: Record<string, Uint8Array>, name: string): Uint8Array | undefined {
  if (files[name]) return files[name];
  const suffix = '/' + name;
  for (const key of Object.keys(files)) {
    if (key.endsWith(suffix)) return files[key];
  }
  return undefined;
}

/**
 * Množina service_id aktivních pro dané datum podle calendar.txt + calendar_dates.txt.
 * Vrací null, pokud feed kalendář vůbec nemá (pak bereme všechny tripy).
 */
function activeServiceIds(
  calendar: CsvTable | null,
  calendarDates: CsvTable | null,
  dateIso: string,
): Set<string> | null {
  if (!calendar && !calendarDates) return null;
  const ymd = dateIso.replace(/-/g, '');
  const active = new Set<string>();
  if (calendar) {
    const sid = getter(calendar, 'service_id');
    const start = getter(calendar, 'start_date');
    const end = getter(calendar, 'end_date');
    const day = getter(calendar, weekdayCol(dateIso));
    for (const row of calendar.rows) {
      // Lexikografické porovnání funguje — YYYYMMDD má pevnou délku.
      if (day(row) === '1' && start(row) <= ymd && ymd <= end(row)) active.add(sid(row));
    }
  }
  if (calendarDates) {
    const sid = getter(calendarDates, 'service_id');
    const date = getter(calendarDates, 'date');
    const ex = getter(calendarDates, 'exception_type');
    for (const row of calendarDates.rows) {
      if (date(row) !== ymd) continue;
      if (ex(row) === '1') active.add(sid(row));
      else if (ex(row) === '2') active.delete(sid(row));
    }
  }
  return active;
}

// ---------------------------------------------------------------------------
// Hlavní import
// ---------------------------------------------------------------------------

interface RawStop {
  name: string;
  lat: number;
  lng: number;
  valid: boolean;
}

interface RawRoute {
  name: string;
  vehicle: TransitVehicle;
  /** '#rrggbb' z route_color, nebo null → paleta. */
  color: string | null;
}

interface RawTrip {
  tripId: string;
  routeId: string;
  serviceId: string;
  dir: '0' | '1';
  wheelchair: string;
}

interface StRow {
  seq: number;
  stopId: string;
  arr: number | null;
  dep: number | null;
}

/**
 * Naparsuje GTFS zip do interní sítě veřejné dopravy.
 *
 * @param bytes obsah .zip souboru
 * @param opts.serviceDate datum provozu 'YYYY-MM-DD' (výchozí: dnes v Europe/Prague)
 * @param opts.maxLines maximum importovaných linek (výchozí 300, nejfrekventovanější)
 */
export function parseGtfsZip(
  bytes: Uint8Array,
  opts?: { serviceDate?: string; maxLines?: number },
): GtfsImportResult {
  const warnings: string[] = [];
  const maxLines = opts?.maxLines ?? 300;
  const serviceDate = opts?.serviceDate ?? todayPrague();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(serviceDate)) {
    throw new Error(`Neplatné datum „${serviceDate}“ — očekávám formát YYYY-MM-DD.`);
  }

  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes);
  } catch {
    throw new Error('Soubor se nepodařilo rozbalit — vyberte platný GTFS archiv (.zip).');
  }

  const REQUIRED = ['stops.txt', 'routes.txt', 'trips.txt', 'stop_times.txt'];
  const missing = REQUIRED.filter((n) => !findFile(files, n));
  if (missing.length > 0) {
    throw new Error(
      `GTFS archiv není úplný — chybí povinné soubory: ${missing.join(', ')}. ` +
        'Vyberte kompletní GTFS export (zip se soubory stops.txt, routes.txt, trips.txt a stop_times.txt).',
    );
  }

  const readCsv = (name: string): CsvTable | null => {
    const f = findFile(files, name);
    return f ? parseCsv(strFromU8(f)) : null;
  };

  // --- stops.txt: jen nástupní místa (location_type 0 nebo prázdné) -------
  const stopsRaw = new Map<string, RawStop>();
  {
    const t = readCsv('stops.txt')!;
    const id = getter(t, 'stop_id');
    const name = getter(t, 'stop_name');
    const lat = getter(t, 'stop_lat');
    const lon = getter(t, 'stop_lon');
    const locType = getter(t, 'location_type');
    for (const row of t.rows) {
      const lt = locType(row);
      if (lt !== '' && lt !== '0') continue; // stanice/vchody/uzly ignorujeme
      const sid = id(row);
      if (!sid) continue;
      const la = Number(lat(row));
      const ln = Number(lon(row));
      const valid =
        lat(row) !== '' &&
        lon(row) !== '' &&
        Number.isFinite(la) &&
        Number.isFinite(ln) &&
        Math.abs(la) <= 90 &&
        Math.abs(ln) <= 180;
      stopsRaw.set(sid, { name: name(row) || sid, lat: la, lng: ln, valid });
    }
  }

  // --- routes.txt ----------------------------------------------------------
  const routesRaw = new Map<string, RawRoute>();
  {
    const t = readCsv('routes.txt')!;
    const id = getter(t, 'route_id');
    const shortName = getter(t, 'route_short_name');
    const longName = getter(t, 'route_long_name');
    const routeType = getter(t, 'route_type');
    const color = getter(t, 'route_color');
    for (const row of t.rows) {
      const rid = id(row);
      if (!rid) continue;
      const c = color(row);
      routesRaw.set(rid, {
        name: shortName(row) || longName(row) || rid,
        vehicle: vehicleFromRouteType(routeType(row)),
        color: /^[0-9a-fA-F]{6}$/.test(c) ? '#' + c : null,
      });
    }
  }

  // --- trips.txt -----------------------------------------------------------
  const trips: RawTrip[] = [];
  const tripsPerService = new Map<string, number>();
  {
    const t = readCsv('trips.txt')!;
    const route = getter(t, 'route_id');
    const service = getter(t, 'service_id');
    const trip = getter(t, 'trip_id');
    const dir = getter(t, 'direction_id');
    const wheelchair = getter(t, 'wheelchair_accessible');
    for (const row of t.rows) {
      const tripId = trip(row);
      if (!tripId) continue;
      const serviceId = service(row);
      trips.push({
        tripId,
        routeId: route(row),
        serviceId,
        dir: dir(row) === '1' ? '1' : '0',
        wheelchair: wheelchair(row),
      });
      tripsPerService.set(serviceId, (tripsPerService.get(serviceId) ?? 0) + 1);
    }
  }

  // --- kalendář: výběr aktivních service_id pro serviceDate ----------------
  let active = activeServiceIds(readCsv('calendar.txt'), readCsv('calendar_dates.txt'), serviceDate);
  if (active !== null && trips.length > 0 && !trips.some((t) => active!.has(t.serviceId))) {
    // Pro dané datum nic nejede — vezmeme službu s nejvíce tripy, ať je co ukázat.
    let best = '';
    let bestCount = -1;
    for (const [sid, count] of tripsPerService) {
      if (count > bestCount) {
        best = sid;
        bestCount = count;
      }
    }
    active = new Set([best]);
    warnings.push(
      `Pro datum ${serviceDate} podle kalendáře nejede žádný spoj — ` +
        `použita služba s nejvíce spoji („${best}“, ${bestCount} spojů).`,
    );
  }
  const activeTrips = active === null ? trips : trips.filter((t) => active!.has(t.serviceId));

  // --- stop_times.txt: JEDEN průchod, skupinování podle trip_id -------------
  // Složitost O(řádky) v čase i paměti — ukládáme jen řádky aktivních tripů.
  const stByTrip = new Map<string, StRow[]>();
  {
    const activeTripIds = new Set(activeTrips.map((t) => t.tripId));
    const t = readCsv('stop_times.txt')!;
    const trip = getter(t, 'trip_id');
    const arr = getter(t, 'arrival_time');
    const dep = getter(t, 'departure_time');
    const stop = getter(t, 'stop_id');
    const seq = getter(t, 'stop_sequence');
    for (const row of t.rows) {
      const tripId = trip(row);
      if (!activeTripIds.has(tripId)) continue;
      let list = stByTrip.get(tripId);
      if (!list) {
        list = [];
        stByTrip.set(tripId, list);
      }
      const seqNum = Number(seq(row));
      list.push({
        seq: Number.isFinite(seqNum) ? seqNum : list.length, // rozbitá sekvence → pořadí v souboru
        stopId: stop(row),
        arr: parseGtfsTimeMin(arr(row)),
        dep: parseGtfsTimeMin(dep(row)),
      });
    }
    for (const list of stByTrip.values()) list.sort((a, b) => a.seq - b.seq);
  }

  // --- skupiny route+direction → kandidáti na linky -------------------------
  interface Group {
    routeId: string;
    dir: '0' | '1';
    order: number;
    tripIds: string[];
    wheelchair: string[];
  }
  const groups = new Map<string, Group>();
  for (const t of activeTrips) {
    const key = `${t.routeId} ${t.dir}`;
    let g = groups.get(key);
    if (!g) {
      g = { routeId: t.routeId, dir: t.dir, order: groups.size, tripIds: [], wheelchair: [] };
      groups.set(key, g);
    }
    g.tripIds.push(t.tripId);
    g.wheelchair.push(t.wheelchair);
  }

  interface Candidate {
    group: Group;
    route: RawRoute;
    stopIds: string[];
    departures: string[];
    speedKmh: number;
    speedFallback: boolean;
    accessible: boolean;
    tripCount: number;
  }
  const candidates: Candidate[] = [];
  for (const g of groups.values()) {
    const route = routesRaw.get(g.routeId);
    if (!route) {
      warnings.push(`Spoje odkazují na neznámou route_id „${g.routeId}“ — vynecháno.`);
      continue;
    }
    // Použitelné tripy: alespoň 2 zastávky v stop_times.
    const usable = g.tripIds.filter((id) => (stByTrip.get(id)?.length ?? 0) >= 2);
    if (usable.length === 0) {
      warnings.push(`Linka „${route.name}“: žádný spoj nemá alespoň dvě zastávky — vynechána.`);
      continue;
    }

    // Reprezentativní trip = nejvyšší počet stop_times (nejčastější/nejúplnější varianta trasy).
    let rep: StRow[] = stByTrip.get(usable[0])!;
    for (const id of usable) {
      const rows = stByTrip.get(id)!;
      if (rows.length > rep.length) rep = rows;
    }

    // Odjezdy z PRVNÍ zastávky všech tripů skupiny — dedup, řazení, HH:MM (mod 24 h).
    const depSet = new Set<string>();
    for (const id of usable) {
      const first = stByTrip.get(id)![0];
      const m = first.dep ?? first.arr;
      if (m !== null) depSet.add(toHHMM(m));
    }
    const departures = [...depSet].sort();
    if (departures.length === 0) {
      warnings.push(`Linka „${route.name}“: žádný spoj nemá čitelný čas odjezdu — vynechána.`);
      continue;
    }

    // Rychlost: medián (haversine délka po zastávkách / doba jízdy) přes spoje.
    const speeds: number[] = [];
    for (const id of usable) {
      const rows = stByTrip.get(id)!;
      const first = rows[0];
      const last = rows[rows.length - 1];
      const t0 = first.dep ?? first.arr;
      const t1 = last.arr ?? last.dep;
      if (t0 === null || t1 === null || t1 <= t0) continue;
      const coords = rows
        .map((r) => stopsRaw.get(r.stopId))
        .filter((s): s is RawStop => !!s && s.valid);
      let dist = 0;
      for (let i = 1; i < coords.length; i++) dist += haversineKm(coords[i - 1], coords[i]);
      if (dist > 0) speeds.push(dist / ((t1 - t0) / 60));
    }
    const med = median(speeds);

    // Bezbariérovost: bez informace → true; jinak podle převahy hodnot 1/2.
    let ones = 0;
    let twos = 0;
    for (const w of g.wheelchair) {
      if (w === '1') ones++;
      else if (w === '2') twos++;
    }

    candidates.push({
      group: g,
      route,
      stopIds: rep.map((r) => r.stopId),
      departures,
      speedKmh: med ?? 20,
      speedFallback: med === null,
      accessible: ones + twos === 0 ? true : ones >= twos,
      tripCount: usable.length,
    });
  }

  // --- limit počtu linek: nejfrekventovanější mají přednost -----------------
  let selected = candidates;
  if (candidates.length > maxLines) {
    const byFrequency = [...candidates].sort(
      (a, b) => b.tripCount - a.tripCount || a.group.order - b.group.order,
    );
    const keep = new Set(byFrequency.slice(0, maxLines));
    warnings.push(
      `Feed obsahuje ${candidates.length} linek — importováno ${maxLines} ` +
        `nejfrekventovanějších, vynecháno ${candidates.length - maxLines}.`,
    );
    selected = candidates.filter((c) => keep.has(c)); // zachovej původní pořadí feedu
  }

  // --- sestavení zastávek a linek -------------------------------------------
  const usedStops = new Map<string, TransitStop>();
  const warnedStops = new Set<string>();
  const lines: TransitLine[] = [];
  const linesByRoute = new Map<string, TransitLine[]>();
  let tripsUsed = 0;

  for (const c of selected) {
    const stopIds: string[] = [];
    for (const sid of c.stopIds) {
      const s = stopsRaw.get(sid);
      if (!s || !s.valid) {
        if (!warnedStops.has(sid)) {
          warnedStops.add(sid);
          warnings.push(
            s
              ? `Zastávka „${s.name}“ (${sid}) má nevalidní souřadnice — vynechána.`
              : `Zastávka „${sid}“ ze stop_times chybí v stops.txt — vynechána.`,
          );
        }
        continue;
      }
      stopIds.push(sid);
      if (!usedStops.has(sid)) {
        usedStops.set(sid, { id: sid, name: s.name, lat: s.lat, lng: s.lng });
      }
    }
    if (stopIds.length < 2) {
      warnings.push(
        `Linka „${c.route.name}“: po vynechání nevalidních zastávek zbývají méně než 2 zastávky — vynechána.`,
      );
      continue;
    }
    if (c.speedFallback) {
      warnings.push(
        `Linka „${c.route.name}“: rychlost nelze odvodit z jízdních dob — použito 20 km/h.`,
      );
    }
    const line: TransitLine = {
      id: `gtfs-${c.group.routeId}-${c.group.dir}`,
      name: c.route.name,
      vehicle: c.route.vehicle,
      // Platná route_color, jinak deterministická paleta podle indexu linky.
      color: c.route.color ?? PALETTE[lines.length % PALETTE.length],
      stopIds,
      departures: c.departures,
      speedKmh: c.speedKmh,
      dwellMin: 0.3,
      costFlat: 0, // cena se doplní v UI globálním tarifem
      emissionsPerKm: EMISSIONS[c.route.vehicle],
      comfort: COMFORT[c.route.vehicle],
      accessible: c.accessible,
      bidirectional: false, // každý směr GTFS je samostatná linka
    };
    lines.push(line);
    tripsUsed += c.tripCount;
    const list = linesByRoute.get(c.group.routeId) ?? [];
    list.push(line);
    linesByRoute.set(c.group.routeId, list);
  }

  // Route s oběma směry a shodným názvem → rozliš příponou konečné zastávky.
  for (const routeLines of linesByRoute.values()) {
    if (routeLines.length === 2 && routeLines[0].name === routeLines[1].name) {
      for (const l of routeLines) {
        const dest = usedStops.get(l.stopIds[l.stopIds.length - 1]);
        if (dest) l.name = `${l.name} → ${dest.name}`;
      }
      if (routeLines[0].name === routeLines[1].name) {
        // Okružní linky se stejnou konečnou — rozliš aspoň směrem.
        routeLines[0].name += ' (tam)';
        routeLines[1].name += ' (zpět)';
      }
    }
  }

  // --- feed_info.txt (volitelné) --------------------------------------------
  let feedName: string | undefined;
  const feedInfo = readCsv('feed_info.txt');
  if (feedInfo && feedInfo.rows.length > 0) {
    const publisher = getter(feedInfo, 'feed_publisher_name')(feedInfo.rows[0]);
    if (publisher) feedName = publisher;
  }

  const network: TransitNetwork = {
    stops: [...usedStops.values()],
    lines,
    maxAccessKm: 1.2,
    transferMin: 3,
    source: 'gtfs', // importovaný reálný jízdní řád
  };
  return {
    network,
    stats: { stops: network.stops.length, lines: lines.length, trips: tripsUsed },
    warnings,
    feedName,
  };
}
